import { useCallback } from 'react';
import type { EdgeDescriptor } from '@bliks-io/ui';
import {
  invalidateDraftCaches,
  isDraftEdgeNotFound,
  useDraftGesture
} from '@/api/mutations/helpers/draftMutation';
import { canPerformEdgeAction, performEdgeAction } from '../edge-actions';
import type { EdgeAction, EdgeActionDeps } from '../edge-actions/types';

export function useEdgeAction(deps: EdgeActionDeps) {
  const runEdgeAction = useDraftGesture(
    deps.processId,
    async (action: EdgeAction, edge: EdgeDescriptor) => {
      try {
        await performEdgeAction(action, edge, deps);
      } catch (err) {
        // Double-fire absorption: a re-fired edge action whose edge was
        // already consumed 409s with DRAFT_EDGE_NOT_FOUND. Soft no-op — no
        // toast, and not a failed save. The transaction restored its snapshot
        // without refetching, so re-sync the cache with server truth here.
        if (isDraftEdgeNotFound(err)) {
          await invalidateDraftCaches(deps.queryClient, deps.processId);
          return;
        }
        throw err;
      }
    }
  );

  return useCallback(
    async (action: EdgeAction, edge: EdgeDescriptor) => {
      // Defensive — the context menu already hides disallowed items. Checked
      // outside the gesture so a rejected action starts no save cycle.
      if (!canPerformEdgeAction(action, edge)) return;
      await runEdgeAction(action, edge);
    },
    [runEdgeAction]
  );
}
